import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { QrCode, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. You can log in now.");
      setTimeout(() => navigate("/login"), 1600);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="max-w-md mx-auto w-full px-4 sm:px-6 flex-1 flex flex-col justify-center py-12">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow">
            <QrCode className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-black text-2xl tracking-tight text-slate-900">Qdurator</span>
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
              <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">
                Password updated
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                Redirecting you to log in…
              </p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="reset-heading">
                Choose a new password
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Pick something you don't use elsewhere.
              </p>

              <form onSubmit={onSubmit} className="mt-6 space-y-5">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                    New password
                  </Label>
                  <Input
                    data-testid="reset-password-input"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="h-11 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                    Confirm password
                  </Label>
                  <Input
                    data-testid="reset-password-confirm-input"
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Type it again"
                    className="h-11 rounded-xl"
                  />
                </div>
                {err && (
                  <div className="text-sm bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg" data-testid="reset-error">
                    {err}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                  className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
